import { useReactiveVar } from '@apollo/client'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import { workspaceSelectedElement } from 'api/graphQL/reactiveVars/element'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { getAssemblyLevel } from 'pages/ProjectsPage/WorkspacePage/components/ElementsPanel/ElementsPanel'
import { useCallback, useMemo } from 'react'
import { openCenteredBrowserWindow } from 'shared/utils/browserUtils'
import {
    getPrintPreviewUrl,
    WorkspacePrintPreview,
} from 'shared/utils/workspaceUtils'
import { useActionBarActions } from '../ActionBarStore'
import { useExportSectionMutation } from '../api/mutations/exportSection'
import { useSupportingDocumentsQuery } from '../api/supportingDocs'
import { FileType, NodeType } from '../types'
import * as utils from '../utils'
import { useShallowSelector } from '../Workspace2Store'
import { useContentItemFetcher } from './useContentItemFetcher'
import { usePermissionChecks } from './usePermissionChecks'
import { useTreeDataMapping } from './useTreeDataMapping'

export const useActionBarHandler = (
    projectId: string,
    projectProductFamilyId: string
) => {
    const { selectedElementNode: selectedProjectElementNode } = useReactiveVar(
        workspaceSelectedElement
    )

    const {
        isStudioManagement,
        activeTab,
        contentItemViewNodes,
        classificationNodes,
        expandedKeys,
        isTreeLoading,
        treeData,
        setTreeData,
        setExpandedKeys,
        setArticlesLoading,
    } = useShallowSelector((s) => ({
        isStudioManagement: s.isContentTeamFromStudioPage,
        activeTab: s.activeTab,
        contentItemViewNodes: s.contentItemViewNodes,
        classificationNodes: s.classificationNodes,
        expandedKeys: s.expandedKeys,
        isTreeLoading: s.isTreeLoading,
        treeData: s.treeData,
        setTreeData: s.actions.setTreeData,
        setExpandedKeys: s.actions.setExpandedKeys,
        setArticlesLoading: s.actions.setArticlesLoading,
    }))

    const memoContentItemViewIds = useMemo(
        () =>
            contentItemViewNodes
                ?.filter((x) => (x.textType as NodeType) === NODE_TYPES.Article)
                .map((x) => x.id ?? '') ?? [],
        [contentItemViewNodes]
    )

    const permission = usePermissionChecks(treeData, selectedProjectElementNode)

    const { fetchContentItemView: fetchProductFamily } = useContentItemFetcher({
        projectId,
        treeData,
        setTreeData,
        permission,
    })

    // Builds the classification nodes from the uniformat classification properties.
    const classificationNodeKeys = useMemo(
        () => classificationNodes?.map((node) => node.key),
        [classificationNodes]
    )
    const onExpandAllAssemblies = useCallback(
        () => setExpandedKeys(classificationNodeKeys ?? []),
        [classificationNodeKeys]
    )
    const onCollapseAllAssemblies = useCallback(() => setExpandedKeys([]), [
        classificationNodeKeys,
    ])

    const { withKeysToFetch, withKeysToCollapse } = useTreeDataMapping()
    const onExpandAll = useCallback(() => {
        // get all keys that are not expanded yet
        const keysToExpand = memoContentItemViewIds.filter(
            (key) => !expandedKeys.includes(key)
        )
        // find article keys to fetch
        const articleKeysToFetch = withKeysToFetch(keysToExpand)
        // fetch the articles if any
        if (articleKeysToFetch.length > 0) {
            setArticlesLoading(articleKeysToFetch) // to indicate loading
            if (fetchProductFamily) {
                fetchProductFamily(
                    utils.getActiveTabLabel(activeTab),
                    articleKeysToFetch
                )
            }
        }
        // expand all content items
        setExpandedKeys([...expandedKeys, ...keysToExpand])
    }, [memoContentItemViewIds, expandedKeys, fetchProductFamily])

    const onCollapseAll = useCallback(() => {
        setExpandedKeys(withKeysToCollapse(treeData))
        setArticlesLoading([])
    }, [treeData])

    const handleViewPrintPreview = useCallback(() => {
        if (selectedProjectElementNode?.elementId && !isStudioManagement) {
            openCenteredBrowserWindow(
                getPrintPreviewUrl({
                    projectId,
                    elementNode: selectedProjectElementNode,
                    projectProductFamilyId,
                } as WorkspacePrintPreview),
                `Preview${projectId}`,
                'directories=no, titlebar=no, location=no, toolbar=no, menubar=no, resizable=yes'
            )
        } else if (isStudioManagement) {
            openCenteredBrowserWindow(
                getPrintPreviewUrl({
                    projectId,
                    elementNode: selectedProjectElementNode,
                    projectProductFamilyId,
                    isStudioManagement,
                } as WorkspacePrintPreview),
                `Preview${projectId}`,
                'directories=no, titlebar=no, location=no, toolbar=no, menubar=no, resizable=yes'
            )
        }
    }, [projectId, selectedProjectElementNode, projectProductFamilyId])

    const handleExpandAll = useCallback(
        () =>
            permission.isAssemblyTypeProject
                ? onExpandAllAssemblies()
                : onExpandAll(),
        [onExpandAllAssemblies, onExpandAll]
    )

    const handleCollapseAll = useCallback(
        () =>
            permission.isAssemblyTypeProject
                ? onCollapseAllAssemblies()
                : onCollapseAll(),
        [onCollapseAllAssemblies, onCollapseAll]
    )

    const { setIsExportLoading } = useActionBarActions()
    const { exportSection } = useExportSectionMutation()
    const handleExport = useCallback(
        (fileType: FileType) => {
            if (
                selectedProjectElementNode?.elementId ||
                (isStudioManagement &&
                    selectedProjectElementNode?.baseElementId)
            ) {
                if (fileType) {
                    setIsExportLoading(fileType, true)
                    exportSection({
                        variables: {
                            exportSectionInput: {
                                projectId,
                                projectProductFamilyId: isStudioManagement
                                    ? selectedProjectElementNode.elementId
                                        ? selectedProjectElementNode.elementId
                                        : selectedProjectElementNode.baseElementId
                                    : projectProductFamilyId
                                    ? projectProductFamilyId
                                    : selectedProjectElementNode.elementId
                                    ? selectedProjectElementNode.elementId
                                    : selectedProjectElementNode.baseElementId,
                                nodeId: selectedProjectElementNode?.id,
                                familyTreePath:
                                    selectedProjectElementNode?.elementType ===
                                    ProjectElementType.PRODUCTFAMILY
                                        ? selectedProjectElementNode?.treePath
                                        : selectedProjectElementNode?.parentTreePath,
                                fileType: fileType,
                            },
                        },
                    })
                }
            }
        },
        [
            projectId,
            selectedProjectElementNode,
            isStudioManagement,
            projectProductFamilyId,
            exportSection,
        ]
    )

    const handleExportWord = useCallback(() => {
        handleExport(FileType.DOCX)
    }, [handleExport])

    const handleExportPDF = useCallback(() => {
        handleExport(FileType.PDF)
    }, [handleExport])

    const { getSupportingDocuments } = useSupportingDocumentsQuery()
    const handleViewSupportingDocs = useCallback(() => {
        getSupportingDocuments({
            variables: {
                getSupportingDocsInput: {
                    productFamilyId: projectProductFamilyId,
                },
            },
        })
    }, [projectProductFamilyId])

    // get all keys that are not expanded yet
    const keysToExpand = useMemo(
        () =>
            permission.isAssemblyTypeProject
                ? classificationNodeKeys?.filter(
                      (key) => !expandedKeys.includes(key)
                  )
                : memoContentItemViewIds?.filter(
                      (key) => !expandedKeys.includes(key)
                  ),
        [memoContentItemViewIds, classificationNodeKeys, expandedKeys]
    )

    const assemblyText = useMemo(() => selectedProjectElementNode?.text ?? '', [
        selectedProjectElementNode,
    ])
    // Gets the assembly level from the assembly text.
    const assemblyLevel = useMemo(
        () => getAssemblyLevel(assemblyText.split('|')[0]),
        [assemblyText]
    )
    // Checks if the assembly level is a division level.
    const isDivisionLevel = useMemo(
        () => assemblyText.split('|')[0]?.length === 2,
        [assemblyText]
    )

    const expandCollapseDisabled = useMemo(
        () =>
            permission.isAssemblyTypeProject &&
            assemblyLevel === 1 &&
            !isDivisionLevel
                ? false
                : isTreeLoading,
        [assemblyLevel, isDivisionLevel, isTreeLoading]
    )

    return {
        handleViewPrintPreview,
        handleExportPDF,
        handleExportWord,
        handleViewSupportingDocs,
        handleExpandAll,
        handleCollapseAll,
        keysToExpand,
        isDivisionLevel,
        assemblyLevel,
        expandCollapseDisabled,
    }
}
